// ─── Guided Setup: badge checking (access correlation), set up in full ────────
//
// The whole PACS config: the doors and the cameras that watch them, the matching
// windows, the badge-id → face mapping, and a paste box to import a day's export
// and prove the mapping actually resolves.
//
// The import is here, in setup, for one reason: until some badge records arrive
// there is nothing to compare, so "it's configured" cannot be distinguished from
// "it's configured wrongly". Pasting one day's export turns a claim into a count.
//
// This module raises no alarm of its own and this panel does not imply it does.
// A mismatch is a finding for a person to look at, because the cost of being
// wrong — accusing an employee of lending their badge — is paid by a person.

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../utils/streamApi';
import { Field, Panel, SettingRow, CameraChips, Advanced, countChanged, useModuleSaver }
  from './SetupBits';

// Only one of these is a question: whether to report people seen with no badge
// at all depends on whether the cameras cover more than the badged doors, which
// is a fact about the site. The match windows and the evidence bar have defaults
// chosen for a reason (see the module docstring) and a customer has no basis to
// move them — those sit under Advanced. `def` matches pacs_access.default_config().
const SETTINGS = [
  { key: 'report_unbadged', label: 'Also report people seen with no badge at all', type: 'bool',
    def: true, ask: true,
    hint: 'Tailgating, a propped door, a guard waving someone through. Turn this OFF if your cameras cover more than the badged doors, or every ordinary walk-past becomes a finding.' },
  { key: 'match_before_secs', label: 'Seen up to this long BEFORE the badge (s)', type: 'int',
    min: 0, max: 300, step: 5, def: 45,
    hint: 'Wide on purpose. A camera timestamp is when the face was CONFIRMED — several frames, on a loaded box — while the badge read is instant. Tightening this to "a badge read is instant" measures the box’s lag, not the person.' },
  { key: 'match_after_secs', label: 'Or this long after (s)', type: 'int',
    min: 0, max: 300, step: 5, def: 30 },
  { key: 'strong_face_conf', label: 'Count the face as strong evidence above', type: 'range',
    min: 0.3, max: 0.9, step: 0.05, def: 0.55,
    hint: 'Labels how solid a finding is; it filters nothing. A weak match still gets reported — marked weak — because hiding it would hide the finding entirely.' },
  { key: 'retention_days', label: 'Keep badge records for (days)', type: 'int',
    min: 1, max: 1825, step: 30, def: 365 },
];

export default function PacsPanel({ cameras, onChanged }) {
  const [cfg,    setCfg]    = useState(null);
  const [denied, setDenied] = useState(false);
  const [door,   setDoor]   = useState(null);     // reader being added / changed
  const [person, setPerson] = useState(null);     // user-map row being added
  const [paste,  setPaste]  = useState('');
  const [imp,    setImp]    = useState(null);     // import result
  const { busy, err, note, setErr, run } = useModuleSaver(apiFetch, onChanged);

  const load = useCallback(async () => {
    try {
      const r = await apiFetch('/ui/pacs/config');
      if (r.status === 403) { setDenied(true); return; }
      if (r.ok) setCfg(await r.json());
    } catch {
      setErr('Could not read the badge settings.');
    }
  }, [setErr]);
  useEffect(() => { load(); }, [load]);

  const reload = async () => { await load(); if (onChanged) await onChanged(); };
  const systems = cfg?.systems || [];
  const users   = cfg?.users || [];
  const camOf   = i => cameras.find(c => c.idx === i)?.name || `Camera ${i}`;

  const saveSetting = (key, val) =>
    run('PUT', '/ui/pacs/settings', { [key]: val }, null).then(d => d && setCfg(d));

  const saveSystems = async rows => {
    if (await run('PUT', '/ui/pacs/systems', { systems: rows }, 'the doors')) await reload();
  };
  const saveUsers = async rows => {
    if (await run('PUT', '/ui/pacs/users', { users: rows }, 'the badge list')) await reload();
  };

  async function doImport() {
    setImp(null);
    const d = await run('POST', '/ui/pacs/events/import', { text: paste }, null);
    if (d) { setImp(d); setPaste(''); }
  }

  if (denied) {
    return (
      <Panel tone="warn" title="Your login cannot configure badge checking">
        It can be switched on, but the doors and badge list need the Access Events
        permission. Ask an administrator to grant it, or to set this part up.
      </Panel>
    );
  }

  return (
    <>
      {!cfg && !err && <div className="gs-panel"><div className="gs-scanning"><span className="gs-spin" /> Loading…</div></div>}

      {cfg && (
        <>
          <div className="gs-section-title">Doors</div>
          <p className="gs-help">
            One row per badge reader, with the cameras that can see the person standing at
            it. The reader id must match what your access-control system puts in its
            records — it is how a badge read is tied to a door, so copy it from the PACS
            rather than inventing one.
          </p>

          {systems.length === 0 && !door && (
            <div className="gs-empty">
              No doors yet. Badge records cannot be matched to anything until there is one.
            </div>
          )}

          {systems.map((s, i) => (
            <div className={`gs-row${s.cameras?.length ? '' : ' gs-row-bad'}`} key={s.system_id}>
              <span className="gs-row-icon">{s.cameras?.length ? '🚪' : '⚠️'}</span>
              <span className="gs-row-text">
                <span className="gs-row-name">{s.door || s.name}{s.active === false ? ' (off)' : ''}</span>
                <span className="gs-row-sub">
                  id {s.system_id} ·{' '}
                  {s.cameras?.length
                    ? s.cameras.map(camOf).join(', ')
                    : 'NO CAMERA — badge reads at this door can never be compared to a face'}
                </span>
              </span>
              <button className="gs-btn small" onClick={() => setDoor({ ...s, _at: i })}>Change</button>
              <button className="gs-btn small ghost" disabled={busy}
                      onClick={() => window.confirm(`Remove the door “${s.door || s.name}”?`)
                        && saveSystems(systems.filter((_, n) => n !== i))}>
                Remove
              </button>
            </div>
          ))}

          {door ? (
            <div className="gs-panel">
              <div className="gs-panel-title">
                {door._at === undefined ? 'New door' : `Change “${door.door || door.name}”`}
              </div>
              <div className="gs-input-row" style={{ maxWidth: 640 }}>
                <Field label="Door name" help="What it is called on site.">
                  <input className="gs-input" value={door.door || ''} maxLength={40}
                         placeholder="e.g. Main Gate"
                         onChange={e => setDoor(d => ({ ...d, door: e.target.value }))} />
                </Field>
                <Field label="Reader id" help="Exactly as the PACS writes it.">
                  <input className="gs-input" value={door.system_id || ''} maxLength={60}
                         placeholder="e.g. DOOR_01" autoCapitalize="none" spellCheck={false}
                         onChange={e => setDoor(d => ({ ...d, system_id: e.target.value }))} />
                </Field>
              </div>
              <Field label="Which cameras can see this door?">
                <CameraChips cameras={cameras} value={door.cameras || []}
                             onChange={v => setDoor(d => ({ ...d, cameras: v }))} />
              </Field>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="gs-btn primary"
                        disabled={busy || !(door.system_id || '').trim() || !(door.cameras || []).length}
                        onClick={() => {
                          const row = { system_id: (door.system_id || '').trim(),
                                        name: (door.door || '').trim() || (door.system_id || '').trim(),
                                        door: (door.door || '').trim(),
                                        cameras: door.cameras, active: door.active !== false };
                          const rows = door._at === undefined
                            ? [...systems, row]
                            : systems.map((s, n) => (n === door._at ? row : s));
                          setDoor(null);
                          saveSystems(rows);
                        }}>
                  Save this door
                </button>
                <button className="gs-btn ghost" onClick={() => { setDoor(null); setErr(null); }}>Cancel</button>
                {!(door.cameras || []).length &&
                  <span className="gs-foot-note">Pick at least one camera</span>}
              </div>
            </div>
          ) : (
            <p className="gs-help gs-help-after">
              <button className="gs-btn small primary"
                      onClick={() => setDoor({ system_id: '', door: '', cameras: [], active: true })}>
                Add a door
              </button>
            </p>
          )}

          <div className="gs-section-title">How badges are matched to faces</div>
          {SETTINGS.filter(s => s.ask).map(s => (
            <SettingRow key={s.key} spec={s} value={cfg[s.key]}
                        onChange={v => saveSetting(s.key, v)} />
          ))}
          <Advanced changed={countChanged(SETTINGS.filter(s => !s.ask), cfg)}>
            {SETTINGS.filter(s => !s.ask).map(s => (
              <SettingRow key={s.key} spec={s} value={cfg[s.key]}
                          onChange={v => saveSetting(s.key, v)} />
            ))}
          </Advanced>

          {/* Identity resolution order, said plainly, because an unmapped id gets
              reported as a configuration gap and NOT as a security finding — and a
              customer who does not know that will read it as an accusation. */}
          <div className="gs-section-title">Badge numbers that are not staff numbers</div>
          <p className="gs-help">
            A badge record names a person by the id your PACS uses. That is matched first
            against this list, then against the employee numbers on the staff list in
            Attendance, and finally treated as a name itself. Most sites need nothing here —
            add a row only for an id that none of those resolve.
          </p>

          {users.map((u, i) => (
            <div className="gs-row" key={u.user_id}>
              <span className="gs-row-icon">🪪</span>
              <span className="gs-row-text">
                <span className="gs-row-name">{u.user_id} → {u.name || '(nobody)'}</span>
                <span className="gs-row-sub">
                  {u.department || 'no department'}{u.badge_id ? ` · badge ${u.badge_id}` : ''}
                </span>
              </span>
              <button className="gs-btn small ghost" disabled={busy}
                      onClick={() => saveUsers(users.filter((_, n) => n !== i))}>Remove</button>
            </div>
          ))}

          {person ? (
            <div className="gs-panel">
              <div className="gs-panel-title">Map a badge id to a person</div>
              <div className="gs-input-row" style={{ maxWidth: 640 }}>
                <Field label="Id in the PACS">
                  <input className="gs-input" value={person.user_id} maxLength={60}
                         autoCapitalize="none" spellCheck={false}
                         onChange={e => setPerson(p => ({ ...p, user_id: e.target.value }))} />
                </Field>
                <Field label="Known face name" help="Must match the enrolled name exactly.">
                  <input className="gs-input" value={person.name} maxLength={60}
                         onChange={e => setPerson(p => ({ ...p, name: e.target.value }))} />
                </Field>
                <Field label="Department">
                  <input className="gs-input" value={person.department} maxLength={40}
                         placeholder="optional"
                         onChange={e => setPerson(p => ({ ...p, department: e.target.value }))} />
                </Field>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button className="gs-btn primary"
                        disabled={busy || !person.user_id.trim() || !person.name.trim()}
                        onClick={() => {
                          const rows = [...users.filter(u => u.user_id !== person.user_id.trim()),
                                        { ...person, user_id: person.user_id.trim(),
                                          name: person.name.trim() }];
                          setPerson(null);
                          saveUsers(rows);
                        }}>
                  Add this mapping
                </button>
                <button className="gs-btn ghost" onClick={() => setPerson(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <p className="gs-help gs-help-after">
              <button className="gs-btn small"
                      onClick={() => setPerson({ user_id: '', name: '', badge_id: '', department: '' })}>
                Add a mapping
              </button>
            </p>
          )}

          <div className="gs-section-title">Check it works</div>
          <p className="gs-help">
            Badge records have to reach this box — pasted here, or posted by an integration
            later. Paste one day of your PACS export (CSV or JSON, with a header row) and
            it will say how many it could read. Importing the same export twice is safe:
            duplicates are skipped, not doubled.
          </p>
          <p className="gs-help">
            It needs a column each for the reader, the record id, the badge, the person and
            the time — the usual export names are recognised automatically.
          </p>
          <textarea className="gs-input" rows={5} value={paste}
                    onChange={e => setPaste(e.target.value)}
                    placeholder="system_id,event_id,badge_id,user_id,timestamp&#10;DOOR_01,99812,4471,1042,2026-10-11 08:31:04"
                    style={{ fontFamily: 'monospace', fontSize: 12.5, resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
            <button className="gs-btn" onClick={doImport} disabled={busy || !paste.trim()}>
              {busy ? <span className="gs-spin" /> : 'Import these records'}
            </button>
          </div>

          {imp && (
            <Panel tone={imp.added ? 'good' : 'warn'}
                   title={imp.added
                     ? `${imp.added} badge record${imp.added === 1 ? '' : 's'} imported`
                     : 'Nothing was imported'}>
              {imp.duplicates ? `${imp.duplicates} were already here and were skipped. ` : ''}
              {imp.rejected
                ? `${imp.rejected} could not be read${(imp.errors || []).length
                    ? ` — first problem: row ${imp.errors[0].row}, ${imp.errors[0].reason}`
                    : ''}.`
                : ''}
              {!imp.added && !imp.duplicates && !imp.rejected
                ? 'The paste had no rows this box could use.' : ''}
              {imp.added
                ? ' The comparison against what the cameras saw is on the Access Events page — '
                  + 'findings there are for a person to judge, and nothing is alerted automatically.'
                : ''}
            </Panel>
          )}

          {note && <p className="gs-help gs-help-after">{note}</p>}
          {err && <div className="gs-err" role="alert">{err}</div>}
        </>
      )}
    </>
  );
}
